import { Group, Text } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { CertificateCategory, CertificateTrigger } from "@tfg/db"
import { ActionIcon } from "@tfg/ui"
import { useMemo } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"

import { EditableCellWithTextInput } from "~/pages/Admin/Translations/columns"

import { SelectCell } from "../SelectCell"
import { MultiSelectCell } from "../Standards/MultiSelectCell"

import {
  useDeleteCertificateTypeMutation,
  useUpdateCertificateTypeMutation,
} from "./hook"

export function useColumns(standardsList: AdminResponses.StandardsList[]) {
  const columnHelper = createColumnHelper<AdminResponses.CertificateType>()
  const { mutate: deleteCertificateType } = useDeleteCertificateTypeMutation()
  const { mutate: updateCertificateType, isLoading: isCreating } =
    useUpdateCertificateTypeMutation()

  return useMemo<ColumnDef<AdminResponses.CertificateType, any>[]>(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor("name", {
        header: "Name",
        cell: ({ getValue, row }) => (
          <EditableCellWithTextInput
            defaultValue={getValue()}
            onSave={(newValue: string) =>
              updateCertificateType({
                id: row.original.id,
                name: newValue,
              })
            }
          />
        ),
      }),
      columnHelper.accessor("trigger", {
        header: "Trigger",
        enableSorting: false,
        cell: ({ getValue, row }) => (
          <SelectCell
            data={Object.values(CertificateTrigger)}
            initialValue={getValue()}
            isCreating={isCreating}
            onChange={(value) => {
              updateCertificateType({
                id: row.original.id,
                trigger:
                  value && value in CertificateTrigger
                    ? (value as CertificateTrigger)
                    : undefined,
              })
            }}
          />
        ),
      }),
      columnHelper.accessor("category", {
        header: "Category",
        enableSorting: false,
        cell: ({ getValue, row }) => (
          <SelectCell
            data={Object.values(CertificateCategory)}
            initialValue={getValue()}
            isCreating={isCreating}
            onChange={(value) => {
              updateCertificateType({
                id: row.original.id,
                category:
                  value && value in CertificateCategory
                    ? (value as CertificateCategory)
                    : undefined,
              })
            }}
          />
        ),
      }),
      columnHelper.accessor("standards", {
        header: "Standards",
        enableSorting: false,
        cell: ({ row }) => {
          const standards = row.original.standards

          return (
            <MultiSelectCell
              data={standardsList.map((standard) => ({
                value: standard.id.toString(),
                label: standard.name,
              }))}
              key={row.original.id}
              initialValues={
                standards
                  ? standards.map((standard) => standard.id.toString())
                  : []
              }
              isLoading={isCreating}
              onChange={(value) => {
                updateCertificateType({
                  id: row.original.id,
                  standard: value.map((v) => parseInt(v)),
                })
              }}
            />
          )
        },
      }),

      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "actions",
        enableSorting: false,
        cell: ({ row }) => (
          <Group wrap="nowrap" gap="xs">
            <ActionIcon
              type="delete"
              onClick={() => deleteCertificateType(row.original.id)}
            />
          </Group>
        ),
      }),
    ],
    [
      columnHelper,
      deleteCertificateType,
      isCreating,
      updateCertificateType,
      standardsList,
    ]
  )
}
